import {
  baseCdcEventDataSchema,
  cdcAuthContextSchema,
  cdcEventModule,
  cdcEventType,
} from '@tempo-io/common-dto';
import { discriminatedUnion, z } from 'zod';

export const adaptivePlannerBaseCdcEventSchema = baseCdcEventDataSchema.extend({
  module: z.literal(cdcEventModule.enum['adaptive-planner']),
});

export const planIdentifierSchema = z.object({
  id: z.number().int(),
});

const fieldChangeSchema = <T extends z.ZodTypeAny>(schema: T) =>
  z.object({ oldValue: schema, newValue: schema });

export const planChangesSchema = z.object({
  id: z.number().int(),
  name: fieldChangeSchema(z.string()).nullish(),
  schedulingValidations: fieldChangeSchema(z.array(z.string())).nullish(),
});

const planEntitySchema = planIdentifierSchema.loose();

export const scenarioIdentifierSchema = z.object({
  id: z.number().int(),
  loopId: z.number().int(),
});

export const scenarioStatusSchema = z.enum([
  'CREATED',
  'ML_INITIATED',
  'ML_VALIDATION_FAILED',
  'ML_VALIDATION_SUCCESS',
  'ML_RECEIVED',
  'TIMEOUT',
]);

const scenarioEntitySchema = scenarioIdentifierSchema
  .extend({
    status: scenarioStatusSchema,
    name: z.string(),
    organizationId: z.string(),
  })
  .loose();

export const planDataChangeDTOSchema = discriminatedUnion('eventType', [
  adaptivePlannerBaseCdcEventSchema.extend({
    entityType: z.literal('plan'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: planEntitySchema,
  }),
  adaptivePlannerBaseCdcEventSchema.extend({
    entityType: z.literal('plan'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: planEntitySchema,
  }),
  adaptivePlannerBaseCdcEventSchema.extend({
    entityType: z.literal('plan'),
    eventType: z.literal(cdcEventType.enum.updated_partial),
    change: planChangesSchema,
  }),
  adaptivePlannerBaseCdcEventSchema.extend({
    entityType: z.literal('plan'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: planIdentifierSchema,
  }),
]);

export const scenarioChangesSchema = z.object({
  id: z.number().int(),
  loopId: z.number().int(),
  status: fieldChangeSchema(z.string()).nullish(),
});

export const scenarioDataChangeDTOSchema = discriminatedUnion('eventType', [
  adaptivePlannerBaseCdcEventSchema.extend({
    entityType: z.literal('scenario'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: scenarioEntitySchema,
  }),
  adaptivePlannerBaseCdcEventSchema.extend({
    entityType: z.literal('scenario'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: scenarioEntitySchema,
  }),
  adaptivePlannerBaseCdcEventSchema.extend({
    entityType: z.literal('scenario'),
    eventType: z.literal(cdcEventType.enum.updated_partial),
    change: scenarioChangesSchema,
  }),
]);

export const planAlertTypeSchema = z.enum([
  'PLAN_SCOPE_CHANGE',
  'PLAN_ITEM_EDITED',
  'ITEM_STORE_SCHEDULE_CHANGES',
]);

export const planAlertIdentifierSchema = z.object({
  id: z.number().int(),
  planId: z.number().int(),
  alertType: planAlertTypeSchema,
});

export const planAlertEntitySchema = planAlertIdentifierSchema.extend({
  createdAt: z.string(),
});

export const planAlertDataChangeDTOSchema = discriminatedUnion('eventType', [
  adaptivePlannerBaseCdcEventSchema.extend({
    entityType: z.literal('plan-alert'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: planAlertEntitySchema,
  }),
  adaptivePlannerBaseCdcEventSchema.extend({
    entityType: z.literal('plan-alert'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: planAlertIdentifierSchema,
  }),
]);

export const scenarioAlertTypeSchema = z.enum([
  'SCENARIO_ITEM_MODIFIED',
  'ITEM_STORE_SCHEDULE_CHANGES',
  'ITEM_STORE_DEPENDENCY_CHANGES',
  'ITEM_STORE_ITEMS_ADDED',
  'ITEM_STORE_ITEMS_DELETED',
  'TEAM_COMPOSITION_UPDATED',
  'ESTIMATION_EFFORT_UPDATED',
]);

export const scenarioAlertIdentifierSchema = z.object({
  id: z.number().int(),
});

export const scenarioAlertEntitySchema = scenarioAlertIdentifierSchema.extend({
  scenarioId: z.number().int(),
  alertType: scenarioAlertTypeSchema,
  createdAt: z.string(),
  createdBy: z.string(),
});

export const scenarioAlertDataChangeDTOSchema = discriminatedUnion('eventType', [
  adaptivePlannerBaseCdcEventSchema.extend({
    entityType: z.literal('scenario-alert'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: scenarioAlertEntitySchema,
  }),
  adaptivePlannerBaseCdcEventSchema.extend({
    entityType: z.literal('scenario-alert'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: scenarioAlertIdentifierSchema,
  }),
]);

export const adaptivePlannerDataChangeDTOSchema = z.object({
  data: baseCdcEventDataSchema.and(
    discriminatedUnion('entityType', [
      planDataChangeDTOSchema,
      scenarioDataChangeDTOSchema,
      planAlertDataChangeDTOSchema,
      scenarioAlertDataChangeDTOSchema,
    ]),
  ),
  authContext: cdcAuthContextSchema,
});

export type AdaptivePlannerDataChangeDTO = z.output<typeof adaptivePlannerDataChangeDTOSchema>;
export type PlanDataChangeDTO = Extract<
  AdaptivePlannerDataChangeDTO['data'],
  { entityType: 'plan' }
>;
export type ScenarioDataChangeDTO = Extract<
  AdaptivePlannerDataChangeDTO['data'],
  { entityType: 'scenario' }
>;
export type PlanAlertDataChangeDTO = Extract<
  AdaptivePlannerDataChangeDTO['data'],
  { entityType: 'plan-alert' }
>;
